import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { invalidateFinancials } from '../../lib/invalidate';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { DECIMAL_INPUT_PATTERN, oreToInputValue, parseMoneyInput } from '../settings/moneyInput';

export type Limit = RouterOutputs['budgetGroup']['list'][number];

/**
 * Create, edit or delete a limit: a monthly amount over a set of categories.
 * `others` are the household's other limits; a category can only be in one.
 */
export function LimitDialog({
  limit,
  others,
  onClose,
}: {
  limit: Limit | null;
  others: Limit[];
  onClose: () => void;
}) {
  const { confirm } = useConfirm();
  const utils = trpc.useUtils();
  const [name, setName] = useState(limit?.name ?? '');
  const [amount, setAmount] = useState(limit ? oreToInputValue(limit.budgetedAmount) : '');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(limit?.categories.map((c) => c.id) ?? []),
  );
  const [formError, setFormError] = useState<string | null>(null);

  const groupsQuery = trpc.category.listGroups.useQuery();

  // Category id → name of the other limit that already has it
  const taken = new Map<string, string>();
  for (const other of others) {
    for (const c of other.categories) taken.set(c.id, other.name);
  }

  const done = (message: string) => {
    void utils.budgetGroup.list.invalidate();
    void invalidateFinancials(utils);
    toast.success(message);
    onClose();
  };
  const onError = (err: { message: string }) => setFormError(err.message);
  const createMutation = trpc.budgetGroup.create.useMutation({
    onSuccess: () => done('Gränsen är skapad'),
    onError,
  });
  const updateMutation = trpc.budgetGroup.update.useMutation({
    onSuccess: () => done('Gränsen är sparad'),
    onError,
  });
  const deleteMutation = trpc.budgetGroup.delete.useMutation({
    onSuccess: () => done('Gränsen är borttagen'),
    onError,
  });
  const pending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const budgetedAmount = parseMoneyInput(amount);
    if (budgetedAmount === null || budgetedAmount <= 0) {
      setFormError('Ange ett belopp per månad, t.ex. 7 000.');
      return;
    }
    if (selected.size === 0) {
      setFormError('Välj minst en kategori som gränsen ska gälla.');
      return;
    }
    setFormError(null);
    const input = { name: name.trim(), budgetedAmount, categoryIds: [...selected] };
    if (limit) updateMutation.mutate({ id: limit.id, ...input });
    else createMutation.mutate(input);
  };

  const handleDelete = async () => {
    if (!limit) return;
    const ok = await confirm({
      title: 'Ta bort gräns',
      message: `Vill du ta bort gränsen "${limit.name}"? Köpen påverkas inte.`,
      confirmLabel: 'Ta bort',
      destructive: true,
    });
    if (ok) deleteMutation.mutate({ id: limit.id });
  };

  const groups = (groupsQuery.data ?? []).filter((g) => g.categories.length > 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{limit ? 'Ändra gräns' : 'Ny gräns'}</DialogTitle>
          <DialogDescription>
            Samma belopp varje månad. Du får en varning när det börjar ta slut.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-[minmax(0,1fr)_7.5rem] gap-3 sm:grid-cols-[1fr_9rem]">
            <div>
              <Label htmlFor="limit-name" className="text-xs text-muted-foreground">
                Namn
              </Label>
              <Input
                id="limit-name"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="t.ex. Mat"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="limit-amount" className="text-xs text-muted-foreground">
                Per månad (kr)
              </Label>
              <Input
                id="limit-amount"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                required
                pattern={DECIMAL_INPUT_PATTERN}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="7 000"
                className="mt-1 tabular-nums"
              />
            </div>
          </div>

          <fieldset>
            <legend className="flex w-full items-baseline justify-between text-xs text-muted-foreground">
              <span>Kategorier</span>
              {selected.size > 0 && <span className="tabular-nums">{selected.size} valda</span>}
            </legend>
            <div className="relative mt-1.5 max-h-72 space-y-3 overflow-y-auto rounded-xl border border-border p-3">
              {groupsQuery.isLoading && <p className="text-sm text-muted-foreground">Laddar…</p>}
              {groups.map((group) => (
                <div key={group.id}>
                  <p className="mb-0.5 text-xs font-medium text-muted-foreground">{group.name}</p>
                  <ul>
                    {group.categories.map((cat) => {
                      const owner = taken.get(cat.id);
                      const inputId = `limit-category-${cat.id}`;
                      return (
                        <li key={cat.id}>
                          <label
                            htmlFor={inputId}
                            className={`flex items-center gap-3 rounded-md px-1 py-2 text-sm sm:py-1.5 ${
                              owner
                                ? 'cursor-not-allowed opacity-60'
                                : 'cursor-pointer hover:bg-muted'
                            }`}
                          >
                            <Checkbox
                              id={inputId}
                              checked={selected.has(cat.id)}
                              disabled={owner != null}
                              onCheckedChange={() => toggle(cat.id)}
                            />
                            <span className="min-w-0 flex-1 truncate">{cat.name}</span>
                            {owner && (
                              <span className="shrink-0 text-xs text-muted-foreground">
                                i {owner}
                              </span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {groupsQuery.isSuccess && groups.length === 0 && (
                <p className="text-sm text-muted-foreground">Inga kategorier att välja.</p>
              )}
            </div>
          </fieldset>

          {formError && (
            <p role="alert" className="text-sm text-negative">
              {formError}
            </p>
          )}

          {/* Phones: Avbryt + Spara side by side, Ta bort below, so the sticky
              footer doesn't eat a third of the sheet. */}
          <DialogFooter className="max-sm:grid max-sm:grid-cols-2">
            {limit && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleDelete}
                disabled={pending}
                className="text-negative hover:bg-negative-soft hover:text-negative max-sm:order-last max-sm:col-span-2 sm:mr-auto"
              >
                Ta bort
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={pending}>
              {limit ? 'Spara' : 'Lägg till'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
